import { InputActions, Layer, ObjectCategory } from "@common/constants";
import { FireCellState, FireConfig } from "@common/definitions/fire";
import { MUSEUM_ATTIC } from "@common/definitions/buildings";
import { Night } from "@common/definitions/night";
import { Container, RenderTexture, Sprite, Texture } from "pixi.js";
import { Game } from "../game";
import { type Player } from "../objects/player";
import { renderInto } from "../utils/canvasBlend";
import { PIXI_SCALE } from "../utils/constants";
import { t } from "../utils/translations/phrases";
import { WarAudio } from "../war/warAudio";
import { AtticDark } from "./atticDark";
import { HALF_H, HALF_W } from "./atticDarkLogic";
import { CameraManager } from "./cameraManager";
import { InputManager } from "./inputManager";
import { beamUv, FanMesh, LIGHT_HEIGHT, LightFans, RoofMaskedLights, roundUv, type UvMap } from "./lightFans";
import { type Cone } from "./lightShadowLogic";
import {
    BEAM_LENGTH, BEAM_WIDTH, BODY_LIFT, DARK_ALPHA, DARK_COLOR, GLOW_SIZE, LAMP_LIGHT, LAMP_TINT, LAMP_WARM, MAP_SCALE,
    NIGHT_ZINDEX, OwnFlashlight, STREAK_LENGTH, STREAK_WIDTH, beamAlpha, bodyAlpha, glowAlpha, streakAlpha
} from "./nightLightLogic";

export { NIGHT_ZINDEX } from "./nightLightLogic";

/**
 * where the night's dark is cut away over the museum's attic (it has a dark of its own): its footprint, less the well
 * over the security office, which its own dark leaves open (the office below is under the night's: 1.06.1's audit, it
 * showed in daylight through the well); a unit a pixel, made once
 */
let atticCutTexture: Texture | undefined;
const atticCut = (): Texture => {
    if (atticCutTexture && !atticCutTexture.destroyed) return atticCutTexture;
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(HALF_W * 2);
    canvas.height = Math.round(HALF_H * 2);
    const ctx = canvas.getContext("2d");
    if (ctx) {
        ctx.fillStyle = "#fff";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        const [x0, y0, x1, y1] = MUSEUM_ATTIC.well;
        ctx.clearRect(x0 + HALF_W, y0 + HALF_H, x1 - x0, y1 - y0);
    }
    return atticCutTexture = Texture.from(canvas);
};

/*
 * Night (1.05, owner decision, 28 October 2026: "in terms of night, thats the mode where there is flashlight, it can be
 * turned on or off by middle mouse press or v (if its available the button and not used as another hotkey) , no line
 * of sight shit"): the map dim under a moonlit blue (1.05.2, the owner: "nightime looks way too dark ... not peach
 * black": nightLightLogic.ts's DARK_ALPHA), plain wherever a light falls. Every player's flashlight
 * (on, as they come in) throws a beam ahead of them, lighting the ground, for everyone who sees them; a shot's muzzle
 * flashes, an explosion lights all round it for a moment, fires glow, tracers streak through the dark. No line of
 * sight: a beam goes through walls and nothing is greyed out. And the dark hides no player (1.05.1, the owner:
 * "Players should show in the dark regardless"): every player shows, their flashlight on or off. 0.98.1 (the owner:
 * "night mode isnt that dark, should be abit darker so the flashlights can also be useful, also, there should be no
 * light that automaticallysurrounds players like it does now, also, add some street lights next to roads"): the dark
 * a little deeper (DARK_ALPHA), the dark taken off each player's body only (`Night.body`: 1.05.1's glow round them lit
 * the steps round everyone), and the street lights' lamps throwing a warm pool of light round them while they stand
 * (`setLamp`, from the obstacles with a `lamp`).
 *
 * The dark is a texture the size of a quarter of the screen each way (`MAP_SCALE`): each frame it's cleared to the dark
 * and every light is erased out of it (Pixi's "erase", on WebGL, WebGPU and the Canvas 2D fallback alike), then it's
 * stretched over the world in the camera container, over every floor's things and under the gas, the damage numbers
 * and the pings (`NIGHT_ZINDEX`). It's drawn at every graphics level: the dark is the game, not a look.
 *
 * The server sends whoever a screen would show by day (definitions/night.ts). In the museum's attic, which has a dark of
 * its own (managers/atticDark.ts), the night's dark is cut away and the beams light the attic's.
 *
 * Your own flashlight switches at once when you press its button (`toggleOwn`), the server's word following; a chip on
 * the HUD says whether it's on and the button for it, and on a phone a button by the menu's switches it.
 *
 * 0.98.7 (the owner: "Lights blocked by walls"): with the shadows on, every light is drawn as a fan cut by the walls
 * on its floor (managers/lightFans.ts): a beam stops at a wall and pours through a doorway, a lamp lights the street
 * and not the room behind the wall, a fire's glow is cut by a crate. With the lighting at High each light also adds
 * its relief (how it strikes the shapes beside it) to the lighting's picture. The dark hides no player still.
 */

/** a moment of light: an explosion's flash or a shot's */
interface Flash {
    x: number
    y: number
    readonly layer: Layer
    readonly radius: number
    readonly start: number
    readonly duration: number
    readonly strength: number
}

/** a beam, as AtticDark draws it into its own dark: where its player is drawn (pixels) and the way they face */
export interface Beam {
    readonly x: number
    readonly y: number
    readonly rotation: number
    readonly layer: Layer
    readonly alpha: number
}

const MAX_FLASHES = 24;

/** a texture drawn pixel by pixel from `alpha(x, y)` (white, that alpha) */
const alphaTexture = (width: number, height: number, alpha: (x: number, y: number) => number): Texture => {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (ctx) {
        const image = ctx.createImageData(width, height);
        const data = image.data;
        for (let y = 0; y < height; y++) {
            for (let x = 0; x < width; x++) {
                const i = (y * width + x) * 4;
                data[i] = data[i + 1] = data[i + 2] = 255;
                data[i + 3] = Math.round(Math.max(0, Math.min(1, alpha(x + 0.5, y + 0.5))) * 255);
            }
        }
        ctx.putImageData(image, 0, 0);
    }
    return Texture.from(canvas);
};

interface Textures {
    readonly beam: Texture
    readonly glow: Texture
    /** a player's body lit, to its edge (0.98.1) */
    readonly body: Texture
    readonly streak: Texture
}

/** a street light's lamp (0.98.1): where its light falls from (game units), on its floor, how far round */
interface Lamp {
    readonly x: number
    readonly y: number
    readonly layer: Layer
    readonly radius: number
}

let textures: Textures | undefined;
/** the light pictures (made once, the first time the night is drawn) */
export const nightTextures = (): Textures => textures ??= {
    // (from its player at the left middle, out to its far end at the right)
    beam: alphaTexture(BEAM_LENGTH, BEAM_WIDTH, (x, y) => {
        const dy = y - BEAM_WIDTH / 2;
        return beamAlpha(Math.hypot(x, dy) / BEAM_LENGTH, Math.atan2(dy, x));
    }),
    glow: alphaTexture(GLOW_SIZE, GLOW_SIZE, (x, y) => glowAlpha(Math.hypot(x - GLOW_SIZE / 2, y - GLOW_SIZE / 2) / (GLOW_SIZE / 2))),
    body: alphaTexture(GLOW_SIZE, GLOW_SIZE, (x, y) => bodyAlpha(Math.hypot(x - GLOW_SIZE / 2, y - GLOW_SIZE / 2) / (GLOW_SIZE / 2))),
    // (its tail at the left, its head at the right)
    streak: alphaTexture(STREAK_LENGTH, STREAK_WIDTH, (x, y) => streakAlpha(x / STREAK_LENGTH, (y - STREAK_WIDTH / 2) / (STREAK_WIDTH / 2)))
};

/**
 * how bright a floor's things show now (its container's alpha, fading as you change floor), 0 if it isn't shown: a light
 * shows where its floor does (from upstairs, the ground's round the building)
 */
const shownOn = (layer: Layer): number => {
    const container = CameraManager.getContainer(layer);
    return container.visible ? container.alpha : 0;
};

/** a burning patch of a fire chunk, as one light: a 2 by 2 block of its cells */
const FIRE_BLOCK = 2;

class NightLightClass {
    private _night = false;
    /** it's a night match (the map said so) */
    get active(): boolean { return this._night; }

    private _overlay?: Sprite;
    /** over the dark: the beams' own warm light, added onto what they show (pixels of the world, in the camera container) */
    private readonly _warm = new Container();
    private readonly _warmPool: Sprite[] = [];
    /** the street lights' lamps standing (0.98.1), by the obstacle each is, and their warm light's sprites */
    private readonly _lamps = new Map<object, Lamp>();
    private readonly _lampPool: Sprite[] = [];
    private _target?: RenderTexture;
    private readonly _scene = new Container();
    private _dark?: Sprite;
    private _atticCut?: Sprite;
    private readonly _pool: Sprite[] = [];
    private _used = 0;
    /** the lights drawn as fans cut by the walls (0.98.7), erased out of the dark, and their warm light in the world */
    private readonly _fans: FanMesh[] = [];
    private _fansUsed = 0;
    private readonly _warmFans: FanMesh[] = [];
    /** where a fan reads its picture, written for each fan as it's set */
    private readonly _uv: UvMap = { u0: 0, ux: 0, uy: 0, v0: 0, vx: 0, vy: 0 };
    /** the light map's corner this frame: where the world's origin is on it (map pixels) */
    private _mapX0 = 0;
    private _mapY0 = 0;
    private _warmFansUsed = 0;
    /** (0.98.7) the lights by their floor, each kept off the roofs shown over it: those erased out of the dark, the warm */
    private readonly _lights = new RoofMaskedLights(this._scene);
    private readonly _warmLights = new RoofMaskedLights(this._warm);
    /** the fans drawn this frame (tests and the lab) */
    private _fanned = 0;
    get fanned(): number { return this._fanned; }
    private readonly _flashes: Flash[] = [];
    private readonly _own = new OwnFlashlight();
    private _chip?: HTMLButtonElement;
    private _mobileButton?: HTMLButtonElement;
    private _chipKey = "";

    /** the map came: a night match or not */
    begin(night: boolean): void {
        this._night = night;
        this._own.reset();
        this._flashes.length = 0;
        if (!night) {
            this._hide();
            this._updateChip(true);
        }
    }

    /** leaving a match */
    reset(): void {
        this._night = false;
        this._own.reset();
        this._flashes.length = 0;
        this._lamps.clear();
        this._hide();
        this._updateChip(true);
    }

    private _hide(): void {
        if (this._overlay) this._overlay.visible = false;
        this._warm.visible = false;
    }

    /** whether `player`'s flashlight shows as on (your own as you switched it, the server's word on its way) */
    flashlightOf(player: Player): boolean {
        const server = player.flashlightOn;
        return player.isActivePlayer && !Game.spectating ? this._own.shown(server, Date.now()) : server;
    }

    /** the flashlight's button: your own off or on (at night, playing) */
    toggleOwn(): void {
        const player = Game.activePlayer;
        // (nor up in the air: the server switches nothing there, and the chip flipped back a moment later: 1.06.1's audit)
        if (!this._night || !player || player.dead || player.air || Game.spectating || Game.gameOver) return;
        this._own.press(this.flashlightOf(player), Date.now());
        InputManager.addAction(InputActions.ToggleFlashlight);
        WarAudio.flashlightClick();
        this._updateChip();
    }

    /**
     * a street light's lamp (0.98.1, an obstacle with a `lamp`, as it comes and as it's shot down): lit at night while
     * it stands, `lamp` its light, or dark (undefined: shot down, or gone from view)
     */
    setLamp(owner: object, lamp: Lamp | undefined): void {
        if (lamp) this._lamps.set(owner, lamp);
        else this._lamps.delete(owner);
    }

    /** the lamps lit now (tests, and the lab) */
    get lampCount(): number { return this._lamps.size; }

    /** the lamps standing (0.98.6: the lighting glows them at dusk, managers/lighting.ts) */
    lamps(): Iterable<Lamp> { return this._lamps.values(); }

    /** an explosion: its flash lights all round it for a moment */
    flash(position: { readonly x: number, readonly y: number }, layer: Layer, radius: number, duration: number, strength = 1): void {
        if (!this._night) return;
        while (this._flashes.length >= MAX_FLASHES) this._flashes.shift();
        this._flashes.push({ x: position.x, y: position.y, layer, radius, start: Date.now(), duration, strength });
    }

    /** a shot: the muzzle flashes, lighting round the gun's end for a blink */
    muzzle(player: Player, length: number): void {
        if (!this._night) return;
        const rotation = player.container.rotation;
        const px = player.container.position.x / PIXI_SCALE, py = player.container.position.y / PIXI_SCALE;
        // (0.98.7's audit: short of a wall in front of the gun, on the shooter's side of it)
        const reach = LightFans.muzzleReach(px, py, rotation, length, player.layer);
        this.flash({ x: px + Math.cos(rotation) * reach, y: py + Math.sin(rotation) * reach }, player.layer, 9, 90, 0.9);
    }

    /** every beam on screen now (AtticDark lights its own dark with them) */
    beams(): Beam[] {
        const beams: Beam[] = [];
        if (!this._night) return beams;
        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            // (nobody in a plane or under a chute lights the ground: 1.06's audit)
            if (player.dead || player.destroyed || player.air || !this.flashlightOf(player)) continue;
            const alpha = shownOn(player.layer);
            if (alpha <= 0.01) continue;
            beams.push({ x: player.container.position.x, y: player.container.position.y, rotation: player.container.rotation, layer: player.layer, alpha });
        }
        return beams;
    }

    /** a light on `layer` erased out of the dark as a picture (map pixels): on its floor's own container */
    private _light(layer: Layer, texture: Texture, x: number, y: number, rotation: number, scaleX: number, scaleY: number, alpha: number, anchorX = 0.5): void {
        let sprite = this._pool[this._used];
        if (!sprite) {
            sprite = new Sprite(texture);
            sprite.blendMode = "erase";
            this._pool.push(sprite);
        }
        this._used++;
        const group = this._lights.group(layer);
        if (sprite.parent !== group) group.addChild(sprite);
        if (sprite.texture !== texture) sprite.texture = texture;
        sprite.anchor.set(anchorX, 0.5);
        sprite.position.set(x, y);
        sprite.rotation = rotation;
        sprite.scale.set(scaleX, scaleY);
        sprite.alpha = alpha;
        sprite.visible = true;
    }

    /** a light's fan on `layer`, erased out of the dark or added over the world: on its floor's own container */
    private _fan(texture: Texture, blend: "erase" | "add", layer: Layer): FanMesh {
        const pool = blend === "erase" ? this._fans : this._warmFans;
        const index = blend === "erase" ? this._fansUsed++ : this._warmFansUsed++;
        let fan = pool[index];
        if (!fan) {
            fan = new FanMesh(texture, blend);
            pool.push(fan);
        }
        const group = (blend === "erase" ? this._lights : this._warmLights).group(layer);
        if (fan.mesh.parent !== group) group.addChild(fan.mesh);
        if (fan.mesh.texture !== texture) fan.mesh.texture = texture;
        fan.mesh.visible = true;
        return fan;
    }

    /**
     * a round light (a lamp's pool, a fire's, a flash's) erased out of the dark: as a fan cut by the walls when the
     * fans are on (and its relief added), round otherwise. `x`, `y` game units; `reach` game units; `map` the light
     * map's measure of a world point; `k` map pixels a unit
     */
    private _glowLight(key: string, x: number, y: number, layer: Layer, reach: number, alpha: number, map: (px: number, py: number) => [number, number], k: number, height: number, strength: number): void {
        const tex = nightTextures();
        const fan = LightFans.fan({ key, x, y, layer, radius: reach, height });
        if (fan && fan.length >= 6) {
            const [cx, cy] = map(x * PIXI_SCALE, y * PIXI_SCALE);
            const mesh = this._fan(tex.glow, "erase", layer);
            // (the fan's points to the light map as they're set: k × a point + the map's corner)
            mesh.set(cx, cy, fan, true, roundUv(cx, cy, reach * k, this._uv), k, this._mapX0, this._mapY0);
            mesh.mesh.alpha = alpha;
            this._fanned++;
        } else {
            const [mx, my] = map(x * PIXI_SCALE, y * PIXI_SCALE);
            const size = (reach * k) / (GLOW_SIZE / 2);
            this._light(layer, tex.glow, mx, my, 0, size, size, alpha);
        }
        LightFans.addRelief(x, y, layer, reach, height, strength * alpha, undefined, fan, true);
    }

    /** a lamp's or a beam's warm light in the world on `layer` (added over what it shows): as a fan when there is one */
    private _warmLight(layer: Layer, texture: Texture, fan: Float32Array | undefined, closed: boolean, x: number, y: number, uv: UvMap, tint: number, alpha: number, fallback: () => void): void {
        if (fan && fan.length >= 6) {
            const mesh = this._fan(texture, "add", layer);
            mesh.set(x, y, fan, closed, uv, PIXI_SCALE);
            mesh.mesh.tint = tint;
            mesh.mesh.alpha = alpha;
        } else fallback();
    }

    /** every frame, after the camera and the attic's dark */
    update(): void {
        this._updateChip();
        if (!this._night || !Game.gameStarted) {
            this._hide();
            return;
        }
        const renderer = Game.pixi.renderer;
        const screen = Game.pixi.screen;
        const width = Math.max(1, Math.ceil(screen.width * MAP_SCALE)), height = Math.max(1, Math.ceil(screen.height * MAP_SCALE));
        if (!this._target) {
            // (dynamic: the dark's sprite takes the light map's new size when the window changes. Without it a sprite
            // keeps its texture's first size, and the dark covered only that much of a bigger window, 1.05.2)
            this._target = RenderTexture.create({ width, height, resolution: 1, scaleMode: "linear", dynamic: true });
            this._dark = new Sprite(Texture.WHITE);
            this._dark.tint = DARK_COLOR;
            this._dark.alpha = DARK_ALPHA;
            this._scene.addChildAt(this._dark, 0);
            this._atticCut = new Sprite(atticCut());
            this._atticCut.anchor.set(0.5);
            this._atticCut.blendMode = "erase";
            this._scene.addChild(this._atticCut);
        } else if (this._target.width !== width || this._target.height !== height) {
            this._target.resize(width, height);
        }
        if (!this._overlay || this._overlay.destroyed) {
            this._overlay = new Sprite(this._target);
            this._overlay.zIndex = NIGHT_ZINDEX;
            this._overlay.eventMode = "none";
        }
        if (this._overlay.parent !== CameraManager.container) CameraManager.addObject(this._overlay);
        if (this._warm.parent !== CameraManager.container) {
            this._warm.zIndex = NIGHT_ZINDEX + 0.5;
            this._warm.eventMode = "none";
            CameraManager.addObject(this._warm);
        }
        const tex = nightTextures();
        const dark = this._dark;
        const cut = this._atticCut;
        if (!dark || !cut) return;
        dark.width = width;
        dark.height = height;

        // the camera: a point of the world (pixels) is on screen at offset + scale × it; on the light map, MAP_SCALE of that
        const scale = CameraManager.container.scale.x;
        const offset = CameraManager.container.position;
        const mapX = (px: number): number => (offset.x + scale * px) * MAP_SCALE;
        const mapY = (py: number): number => (offset.y + scale * py) * MAP_SCALE;
        /** map pixels per game unit */
        const k = scale * PIXI_SCALE * MAP_SCALE;
        // (a fan's points, game units, are k × them + this corner on the light map: FanMesh.set)
        this._mapX0 = offset.x * MAP_SCALE;
        this._mapY0 = offset.y * MAP_SCALE;
        const now = Date.now();

        // (0.98.8: the lights drawn last frame and not this frame are hidden after the lights, the rest left shown:
        // hidden and shown again every frame, the warm fans had Pixi build the world's whole draw list again every frame)
        const usedBefore = this._used, fansBefore = this._fansUsed, warmFansBefore = this._warmFansUsed;
        this._used = 0;
        this._fansUsed = 0;
        this._warmFansUsed = 0;
        this._fanned = 0;
        let warm = 0;
        const map = (px: number, py: number): [number, number] => [mapX(px), mapY(py)];

        // every flashlight on: its beam; every player's body (and nothing round it)
        for (const player of Game.objects.getCategory(ObjectCategory.Player)) {
            // (in a plane or under a chute: up over the night's dark, moonlit, their beam on nothing: 1.06's audit)
            if (player.dead || player.destroyed || player.air) continue;
            const shown = shownOn(player.layer);
            if (shown <= 0.01) continue;
            // (up in the museum's attic, nothing of a light below shows through its boards: only down its well, 1.06.1)
            if (player.layer !== Layer.Upstairs && AtticDark.floorHides(player.position)) continue;
            const { x, y } = player.container.position;
            // (0.98.8: a beam that can't reach the screen isn't made: its fan, its light and its relief were, for every
            // flashlight the page knows)
            const beamReach = Night.reach * k;
            const beamShown = !(mapX(x) < -beamReach || mapY(y) < -beamReach || mapX(x) > width + beamReach || mapY(y) > height + beamReach);
            if (beamShown && this.flashlightOf(player)) {
                const length = Night.reach * k;
                const rotation = player.container.rotation;
                // (0.98.7: the beam a fan cut by the walls, where the fans are on)
                const cone: Cone = { angle: rotation, half: Night.halfAngle };
                const fan = LightFans.fan({ key: `beam:${player.id}`, x: x / PIXI_SCALE, y: y / PIXI_SCALE, layer: player.layer, radius: Night.reach, cone });
                const cos = Math.cos(rotation), sin = Math.sin(rotation);
                if (fan && fan.length >= 4) {
                    const cx = mapX(x), cy = mapY(y);
                    const mesh = this._fan(tex.beam, "erase", player.layer);
                    mesh.set(cx, cy, fan, false, beamUv(cx, cy, cos, sin, length, length * (BEAM_WIDTH / BEAM_LENGTH), this._uv), k, this._mapX0, this._mapY0);
                    mesh.mesh.alpha = shown;
                    this._fanned++;
                } else {
                    this._light(player.layer, tex.beam, mapX(x), mapY(y), rotation, length / BEAM_LENGTH, length / BEAM_LENGTH, shown, 0);
                }
                // (and its warm light over what it shows: a flashlight's, not daylight through a hole)
                const worldLength = Night.reach * PIXI_SCALE;
                const worldWidth = worldLength * (BEAM_WIDTH / BEAM_LENGTH);
                this._warmLight(player.layer, tex.beam, fan, false, x, y, beamUv(x, y, cos, sin, worldLength, worldWidth, this._uv), 0xffd59a, 0.085 * shown, () => {
                    let glow = this._warmPool[warm];
                    if (!glow) {
                        glow = new Sprite(tex.beam);
                        glow.anchor.set(0, 0.5);
                        glow.blendMode = "add";
                        glow.tint = 0xffd59a;
                        this._warmPool.push(glow);
                    }
                    warm++;
                    const group = this._warmLights.group(player.layer);
                    if (glow.parent !== group) group.addChild(glow);
                    glow.visible = true;
                    glow.position.set(x, y);
                    glow.rotation = rotation;
                    glow.scale.set(worldLength / BEAM_LENGTH);
                    glow.alpha = 0.085 * shown;
                });
                LightFans.addRelief(x / PIXI_SCALE, y / PIXI_SCALE, player.layer, Night.reach, LIGHT_HEIGHT.beam, 0.7 * shown, cone, fan, false);
            }
            // every player seen in the dark, their flashlight on or off (1.05.1), and no light round them (0.98.1, the
            // owner: "there should be no light that automaticallysurrounds players"): the dark off their body only
            const body = (Night.body * player.sizeMod * k) / (GLOW_SIZE / 2);
            // (on their floor's container: a player under a roof shown over them is under it, at night as by day)
            this._light(player.layer, tex.body, mapX(x), mapY(y), 0, body, body, BODY_LIFT * shown);
        }

        // the street lights (0.98.1): a pool of light round each lamp still standing, warm as a sodium lamp's
        let lamps = 0;
        for (const lamp of this._lamps.values()) {
            const shown = shownOn(lamp.layer);
            if (shown <= 0.01) continue;
            const px = lamp.x * PIXI_SCALE, py = lamp.y * PIXI_SCALE;
            const mx = mapX(px), my = mapY(py), reach = lamp.radius * k;
            // (only those whose light reaches the screen)
            if (mx < -reach || my < -reach || mx > width + reach || my > height + reach) continue;
            const key = `lamp:${lamp.x.toFixed(1)}:${lamp.y.toFixed(1)}`;
            this._glowLight(key, lamp.x, lamp.y, lamp.layer, lamp.radius, LAMP_LIGHT * shown, map, k, LIGHT_HEIGHT.lamp, 0.8);
            const worldReach = lamp.radius * PIXI_SCALE;
            this._warmLight(lamp.layer, tex.glow, LightFans.fan({ key, x: lamp.x, y: lamp.y, layer: lamp.layer, radius: lamp.radius, height: LIGHT_HEIGHT.lamp }), true, px, py,
                roundUv(px, py, worldReach, this._uv), LAMP_TINT, LAMP_WARM * shown, () => {
                    let pool = this._lampPool[lamps];
                    if (!pool) {
                        pool = new Sprite(tex.glow);
                        pool.anchor.set(0.5);
                        pool.blendMode = "add";
                        pool.tint = LAMP_TINT;
                        this._lampPool.push(pool);
                    }
                    lamps++;
                    const group = this._warmLights.group(lamp.layer);
                    if (pool.parent !== group) group.addChild(pool);
                    pool.visible = true;
                    pool.position.set(px, py);
                    pool.scale.set(worldReach / (GLOW_SIZE / 2));
                    pool.alpha = LAMP_WARM * shown;
                });
        }
        for (let i = lamps; i < this._lampPool.length; i++) this._lampPool[i].visible = false;

        // the flashes: bright at once, fading out
        for (let i = this._flashes.length - 1; i >= 0; i--) {
            const flash = this._flashes[i];
            const age = (now - flash.start) / flash.duration;
            if (age >= 1) {
                this._flashes.splice(i, 1);
                continue;
            }
            const shown = shownOn(flash.layer);
            if (shown <= 0.01) continue;
            // (0.98.8: only those whose light reaches the screen, as the lamps')
            const fx = mapX(flash.x * PIXI_SCALE), fy = mapY(flash.y * PIXI_SCALE), fr = flash.radius * k;
            if (fx < -fr || fy < -fr || fx > width + fr || fy > height + fr) continue;
            this._glowLight(`flash:${flash.start}:${i}`, flash.x, flash.y, flash.layer, flash.radius, flash.strength * (1 - age) * (1 - age) * shown, map, k, LIGHT_HEIGHT.flash, 1.1);
        }

        // fires glow (the ground floor's): a light on each 2 by 2 block of a chunk's cells with anything burning
        const fireShown = shownOn(Layer.Ground);
        if (fireShown > 0.01) {
            const cells = FireConfig.chunkCells, cell = FireConfig.cellSize;
            const fireReach = Night.fireLight * k;
            for (const chunk of Game.objects.getCategory(ObjectCategory.Fire)) {
                if (chunk.destroyed || chunk.burningCells === 0) continue;
                // (0.98.8: only a chunk whose light reaches the screen, as the lamps')
                const cx0 = mapX(chunk.chunkX * cells * cell * PIXI_SCALE), cy0 = mapY(chunk.chunkY * cells * cell * PIXI_SCALE);
                const cx1 = mapX((chunk.chunkX + 1) * cells * cell * PIXI_SCALE), cy1 = mapY((chunk.chunkY + 1) * cells * cell * PIXI_SCALE);
                if (cx1 < -fireReach || cy1 < -fireReach || cx0 > width + fireReach || cy0 > height + fireReach) continue;
                for (let by = 0; by < cells; by += FIRE_BLOCK) {
                    for (let bx = 0; bx < cells; bx += FIRE_BLOCK) {
                        let burning = 0;
                        for (let dy = 0; dy < FIRE_BLOCK; dy++) {
                            for (let dx = 0; dx < FIRE_BLOCK; dx++) {
                                const state = chunk.cellState(bx + dx, by + dy);
                                if (state === FireCellState.Burning) burning += 1;
                                else if (state === FireCellState.Kindling) burning += 0.5;
                            }
                        }
                        if (burning === 0) continue;
                        const wx = (chunk.chunkX * cells + bx + FIRE_BLOCK / 2) * cell;
                        const wy = (chunk.chunkY * cells + by + FIRE_BLOCK / 2) * cell;
                        const flicker = 0.82 + 0.12 * Math.sin(now * 0.017 + wx * 0.7 + wy * 1.3);
                        this._glowLight(`fire:${wx}:${wy}`, wx, wy, Layer.Ground, Night.fireLight, Math.min(1, 0.55 + 0.15 * burning) * flicker * fireShown, map, k, LIGHT_HEIGHT.fire, 0.6);
                    }
                }
            }
        }

        for (let i = warm; i < this._warmPool.length; i++) this._warmPool[i].visible = false;
        this._warm.visible = warm + lamps + this._warmFansUsed > 0;

        // tracers streak through the dark (each lighting a hair round itself)
        for (const bullet of Game.bullets) {
            const glow = bullet.glow;
            if (!glow) continue;
            const shown = shownOn(bullet.layer);
            if (shown <= 0.01) continue;
            const length = Math.max(1, glow.length * k);
            this._light(bullet.layer, tex.streak, mapX(glow.x * PIXI_SCALE), mapY(glow.y * PIXI_SCALE), glow.angle, length / STREAK_LENGTH, (0.9 * k) / (STREAK_WIDTH / 2), Math.min(1, glow.alpha) * 0.7 * shown, 1);
        }

        for (let i = this._used; i < usedBefore; i++) this._pool[i].visible = false;
        for (let i = this._fansUsed; i < fansBefore; i++) this._fans[i].mesh.visible = false;
        for (let i = this._warmFansUsed; i < warmFansBefore; i++) this._warmFans[i].mesh.visible = false;

        // the museum's attic has a dark of its own: the night's is cut away over it (its beams are drawn into its own)
        const attic = AtticDark.area();
        if (attic) {
            cut.visible = true;
            cut.position.set(mapX(attic.x), mapY(attic.y));
            cut.rotation = attic.rotation;
            cut.width = attic.width * scale * MAP_SCALE;
            cut.height = attic.height * scale * MAP_SCALE;
        } else cut.visible = false;

        // (0.98.7: the roofs shown now masked out of each floor's lights: the dark's in the light map's pixels, a world
        // pixel at (offset + scale × it) × MAP_SCALE; the warm light in the world's)
        this._lights.update(offset.x * MAP_SCALE, offset.y * MAP_SCALE, scale * MAP_SCALE);
        this._warmLights.update();
        renderInto(renderer, this._scene, this._target);

        // stretched over the screen: in the camera container, where the screen's top left corner is, its size
        const overlay = this._overlay;
        overlay.visible = true;
        overlay.position.set(-offset.x / scale, -offset.y / scale);
        overlay.scale.set(screen.width / scale / width, screen.height / scale / height);
    }

    /** the HUD's chip (and a phone's button): whether your flashlight is on, and its button */
    private _updateChip(hide = false): void {
        const player = Game.activePlayer;
        const playing = !hide && this._night && Game.gameStarted && !Game.gameOver && !Game.spectating && player !== undefined && !player.dead && player.air === undefined;
        const on = playing && player ? this.flashlightOf(player) : false;
        const keys = InputManager.binds.getInputsBoundToAction("flashlight").filter(key => key !== "");
        const key = `${playing}|${on}|${keys.join(",")}|${InputManager.isMobile}`;
        if (key === this._chipKey) return;
        this._chipKey = key;
        if (InputManager.isMobile) {
            const button = this._mobileButton ?? (playing ? this._makeMobileButton() : undefined);
            if (button) {
                button.hidden = !playing;
                button.classList.toggle("off", !on);
                button.title = on ? t("Flashlight on: tap to turn it off") : t("Flashlight off: tap to turn it on");
                button.setAttribute("aria-pressed", String(on));
            }
            return;
        }
        const chip = this._chip ?? (playing ? this._makeChip() : undefined);
        if (!chip) return;
        chip.hidden = !playing;
        if (!playing) return;
        chip.classList.toggle("off", !on);
        chip.setAttribute("aria-pressed", String(on));
        const label = chip.querySelector(".tf-state");
        if (label) label.textContent = on ? t("FLASHLIGHT ON") : t("FLASHLIGHT OFF");
        const hint = chip.querySelector(".tf-key");
        const pretty = (input: string): string => input === "Mouse1" ? t("Middle click") : input === "Mouse0" ? t("Left click") : input === "Mouse2" ? t("Right click") : input.length === 1 ? input.toUpperCase() : input;
        if (hint) hint.textContent = keys.length ? keys.slice(0, 2).map(pretty).join(" / ") : t("not set");
        chip.title = on ? t("Your flashlight is on: it lights the way ahead, and others see its beam") : t("Your flashlight is off: the way ahead stays dim");
    }

    /** a press on the chip or the phone's button switches it (and never reaches the game: no shot fired) */
    private _wire(element: HTMLElement, release: "click" | "pointerup"): void {
        for (const type of ["pointerdown", "mousedown", "mouseup", "click", "pointerup"]) element.addEventListener(type, event => event.stopPropagation());
        element.addEventListener(release, () => {
            // (a clicked button gives the keyboard back to the game)
            element.blur();
            this.toggleOwn();
        });
    }

    /** the desktop HUD's chip, under the players counter (and the controls chip), made the first night */
    private _makeChip(): HTMLButtonElement | undefined {
        const gameUi = document.getElementById("game-ui");
        if (!gameUi) return undefined;
        const chip = this._chip = document.createElement("button");
        chip.type = "button";
        chip.id = "th-flashlight-chip";
        // (in Arabic its words and keys read right to left: a button isn't among the boxes that do by themselves, 1.06.1)
        chip.className = "rtl-words";
        chip.hidden = true;
        chip.innerHTML = `${FLASHLIGHT_ICON}<span class="tf-state"></span><span class="tf-key"></span>`;
        this._wire(chip, "click");
        const after = document.getElementById("th-controls-chip") ?? document.getElementById("counter-holder");
        if (after?.parentElement) after.after(chip);
        else gameUi.appendChild(chip);
        return chip;
    }

    /** a phone's button, with the menu's (at night) */
    private _makeMobileButton(): HTMLButtonElement | undefined {
        const options = document.getElementById("mobile-options");
        if (!options) return undefined;
        const button = this._mobileButton = document.createElement("button");
        button.type = "button";
        button.id = "btn-flashlight";
        button.className = "btn btn-lg btn-primary";
        button.hidden = true;
        button.innerHTML = FLASHLIGHT_ICON;
        this._wire(button, "pointerup");
        options.appendChild(button);
        return button;
    }
}

/** a flashlight, its beam's rays shown while it's on (CSS: `.off .tf-rays`) */
const FLASHLIGHT_ICON = '<svg class="tf-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M2.5 9.2h8.6l3.6-2.6v10.8l-3.6-2.6H2.5z" fill="currentColor"/><path class="tf-rays" d="M17.4 8.2l4-2.1M17.8 12h4.4M17.4 15.8l4 2.1" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>';

export const NightLight = new NightLightClass();
